import { LucideX } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { useMap } from 'react-map-gl'
import { aIsMapEasing, aMarkLocation, useSetAtom } from '~/atoms'
import { Input } from '~/components/ui'
import { useGeocodeSearch } from '~/hooks/map'
import { cn } from '~/lib'
import { getMapCardSizes } from '~/lib.client'

export default function MapSearch({ className }: { className?: string }) {
  const mapRef = useMap()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()
  const setMarkLocation = useSetAtom(aMarkLocation)
  const setIsMapEasing = useSetAtom(aIsMapEasing)
  const { setSearch, search, predictions, isLoading, getPlaceData } =
    useGeocodeSearch()
  const [isFocused, setIsFocused] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    if (predictions.length === 0) {
      setActiveIndex(-1)
      return
    }

    setActiveIndex((currentIndex) => {
      if (currentIndex < 0) {
        return 0
      }

      return Math.min(currentIndex, predictions.length - 1)
    })
  }, [predictions])

  const onSelectedPrediction = useCallback(
    async (prediction: google.maps.places.AutocompletePrediction) => {
      const placeData = await getPlaceData(prediction)
      if (!placeData) {
        // error
      } else {
        const { lng, lat } = placeData
        setMarkLocation({ lng, lat })
        setIsMapEasing(true)
        mapRef.current!.easeTo({
          // zoom: zoom > 16 ? 16 : zoom,
          zoom: 18,
          center: [lng, lat],
          padding: {
            left: getMapCardSizes().list,
            top: 0,
            right: 0,
            bottom: 0,
          },
          duration: 500,
        })
        setTimeout(() => {
          setIsMapEasing(false)
        }, 510)
      }
      setSearch('')
      setActiveIndex(-1)
      setIsFocused(false)
    },
    [getPlaceData, mapRef, setIsMapEasing, setMarkLocation, setSearch]
  )

  const showResults = isFocused && search.trim().length > 0

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative -m-1 w-40 bg-transparent p-1 lg:w-40 xl:w-64',
        className
      )}>
      <label htmlFor="map-search" className="sr-only">
        Search address
      </label>
      <Input
        ref={inputRef}
        id="map-search"
        value={search}
        className="w-full border-0 pr-8 text-base shadow-md"
        placeholder="Search Address..."
        aria-label="Search address"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={showResults}
        aria-controls={listboxId}
        aria-activedescendant={
          activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined
        }
        onChange={(event) => {
          setSearch(event.target.value)
          setIsFocused(true)
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={(event) => {
          if (
            event.relatedTarget instanceof Node &&
            containerRef.current?.contains(event.relatedTarget)
          ) {
            return
          }

          setIsFocused(false)
        }}
        onKeyDown={(event) => {
          if (predictions.length === 0) {
            if (event.key === 'Escape') {
              setIsFocused(false)
            }
            return
          }

          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setIsFocused(true)
            setActiveIndex((currentIndex) =>
              currentIndex < predictions.length - 1 ? currentIndex + 1 : 0
            )
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault()
            setIsFocused(true)
            setActiveIndex((currentIndex) =>
              currentIndex > 0 ? currentIndex - 1 : predictions.length - 1
            )
          }

          if (event.key === 'Enter' && activeIndex >= 0) {
            event.preventDefault()
            void onSelectedPrediction(predictions[activeIndex]!)
          }

          if (event.key === 'Escape') {
            setIsFocused(false)
          }
        }}
      />
      {search.length > 0 && (
        <button
          type="button"
          aria-label="Clear address search"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setSearch('')
            setActiveIndex(-1)
            setIsFocused(false)
            inputRef.current?.focus()
          }}
          className="absolute right-0 top-1 h-10 px-3 text-black">
          <LucideX className="h-4 w-4" />
          <span className="sr-only">Clear address search</span>
        </button>
      )}

      {showResults && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute left-1 right-1 top-full z-10 mt-1 overflow-hidden rounded-md border bg-background shadow-md">
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Searching addresses...
            </div>
          ) : predictions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No addresses found
            </div>
          ) : (
            <div className="max-h-[300px] overflow-y-auto py-1">
              {predictions.map((prediction, index) => {
                const mainText = prediction.structured_formatting.main_text
                const secondaryText =
                  prediction.structured_formatting.secondary_text

                return (
                  <button
                    key={prediction.place_id}
                    id={`${listboxId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    tabIndex={-1}
                    className={cn(
                      'flex w-full flex-col items-start px-3 py-2 text-left text-sm',
                      index === activeIndex
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent/50'
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => {
                      void onSelectedPrediction(prediction)
                    }}>
                    <span>{mainText}</span>
                    {secondaryText && (
                      <span className="text-xs text-muted-foreground">
                        {secondaryText}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
